import { Component, Inject, Injector, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { MenuItem, MessageService } from 'primeng/api';
import { Observable, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { cloneDeep } from 'lodash';

import { buildSlideInAnimationTrigger, FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { AbstractDialogComponent } from '../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { ScrollPanelComponent } from '../../../Common/Basic/components/ScrollPanel/scroll-panel.component';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../../Common/Dialogs/models/confirm-dialog.model';

import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { PanelPositionEnum } from '../../../Common/Forms/components/KebabMenu/kebab-menu.component';

import { ViDimensionEnum } from '../../enums/vi-dimension.enum';
import { ViQuestionApi } from '../../../api/visibilityIntelligence/models/vi-question.api.model';

import { buildDimensionCounts } from '../../../api/visibilityIntelligence/models/vi-generate-questions.api.model';

import { ViMonitor } from '../../models/vi-monitor.model';
import { ViMonitorService } from '../../services/vi-monitor.service';
import { ViDimensionCardComponent } from '../ViDimensionCard/vi-dimension-card.component';
import { ViManuallyAddQuestionPanelComponent } from '../ViManuallyAddQuestionPanel/vi-manually-add-question-panel.component';
import { ViGenerateAdditionalQuestionsPanelComponent } from '../ViGenerateAdditionalQuestionsPanel/vi-generate-additional-questions-panel.component';
import { ViDimensionCard } from '../../models/vi-dimension-card.model';

@Component({
    selector: 'agpr-vi-query-setup-dialog',
    templateUrl: './vi-query-setup-dialog.component.html',
    styleUrls: ['./vi-query-setup-dialog.component.scss'],
    providers: [],
    standalone: false
})
export class ViQuerySetupDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() monitor: ViMonitor;

    @ViewChild('reviewQuestionScrollPanel') reviewQuestionScrollPanel?: ScrollPanelComponent;
    @ViewChildren(ViDimensionCardComponent) dimensionCardComponents?: QueryList<ViDimensionCardComponent>;

    readonly id = 'viQuerySetupDialog';
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverities = messageSeverityEnum;
    readonly panelPosition = PanelPositionEnum;

    readonly questionCountLimit: number = 25;
    readonly questionDefaultCount: number = 10;

    readonly dimensions: any[] = Object.values(ViDimensionEnum).map(dimension => ({
        key: dimension,
        label: `VisibilityIntelligence.querySetup.dimensions.${dimension}`,
        count: this.questionDefaultCount
    }));

    readonly actions: MenuItem[] = [
        {
            label: this.localizationService.transform('VisibilityIntelligence.querySetup.reviewQuestion.actions.generateAdditional'),
            command: () => this.openAddAdditionalQuestionsPanel()
        },
        {
            label: this.localizationService.transform('VisibilityIntelligence.querySetup.reviewQuestion.actions.manuallyAdd'),
            command: () => this.openManuallyAddQuestionPanel()
        }
    ];

    confirmed: boolean = false;
    hideSlideOverlay: boolean = false;
    isCreateQuestionPage: boolean = true;

    subscriptions: Subscription[] = [];

    // Generate Questions Page
    canGenerate: boolean = this.dimensions.some(dimension => dimension.count > 0);
    isGenerating: boolean = false;

    // Review Questions Page
    isExpanded: boolean = true;
    questions: ViQuestionApi[] = [];
    dimensionCards: ViDimensionCard[] = [];
    selectedQuestion: ViQuestionApi | null = null;
    hasInvalidQuestions: boolean = false;

    constructor(
        public coreDialogService: CoreDialogService,
        public currentUserStateService: CurrentUserStateService,
        public localizationService: LocalizationService,
        public messageService: MessageService,
        public injector: Injector,
        private viMonitorService: ViMonitorService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
    ) {
        super(coreDialogService, 'VIQuerySetupDialog');
    }

    ngOnInit(): void {
        this.setQuestions(cloneDeep(this.monitor?.questions ?? []));
    }

    onQuestionCountChange(): void {
        this.canGenerate = this.dimensions.some(dimension => dimension.count > 0);
    }

    generateQuestions(): void {
        if (!this.canGenerate || this.isGenerating || !this.monitor) {
            return;
        }

        this.isGenerating = true;
        this.viMonitorService.generateQuestions(this.monitor, this.questions, buildDimensionCounts(this.dimensions)).pipe(
            finalize(() => this.isGenerating = false)
        ).subscribe(
            (response) => this.setQuestions(response.questions)
        );
    }

    toggleExpansion() {
        this.isExpanded = !this.isExpanded;
        this.dimensionCards.forEach(card => card.isExpanded = this.isExpanded);
    }

    openAddAdditionalQuestionsPanel() {
        this.coreDialogService.open(ViGenerateAdditionalQuestionsPanelComponent, {
            inputs: {
                overlayContainer: this.overlayContainer,
                animationTrigger: buildSlideInAnimationTrigger(),
                monitor: this.monitor,
                questions: this.questions
            },
            outputs: {
                dialogClose: (newQuestions: ViQuestionApi[] | null) => this.appendQuestions(newQuestions)
            }
        });
    }

    openManuallyAddQuestionPanel() {
        this.coreDialogService.open(ViManuallyAddQuestionPanelComponent, {
            inputs: {
                overlayContainer: this.overlayContainer,
                animationTrigger: buildSlideInAnimationTrigger()
            },
            outputs: {
                dialogClose: (newQuestions: ViQuestionApi[] | null) => this.appendQuestions(newQuestions, true)
            }
        });
    }

    selectQuestion(question: ViQuestionApi) {
        this.selectedQuestion = question;
    }

    questionChanged() {
        this.validateQuestions();
        this.constructDimensionCards();
    }

    deleteQuestion(question: ViQuestionApi) {
        const confirmConfig: ConfirmDialogModel = {
            inputs: {
                id: 'viQuerySetupDialog_deleteQuestionConfirm',
                heading: 'VisibilityIntelligence.querySetup.reviewQuestion.deleteConfirm.heading',
                message: this.localizationService.transform(
                    'VisibilityIntelligence.querySetup.reviewQuestion.deleteConfirm.message',
                    { question: question.question }
                ),
                okLabel: 'Core.button.delete'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (!confirmed) {
                        return;
                    }

                    if (this.selectedQuestion === question) {
                        this.selectedQuestion = null;
                    }

                    this.setQuestions(this.questions.filter(item => item !== question));

                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
    }

    done(): void {
        if (this.hasInvalidQuestions) {
            this.scrollFirstErrorIntoView();
            return;
        }
        this.monitor.questions = this.questions;
        this.confirmed = true;
        this.hideSlideOverlay = true;
    }

    cancel(): void {
        this.hideSlideOverlay = true;
    }

    close(overlayVisibility: boolean): void {
        if (!overlayVisibility) {
            this.closeDialog(this.confirmed);
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(x => x.unsubscribe());
    }

    private setQuestions(questions: ViQuestionApi[]) {
        this.questions = questions ?? [];
        this.isCreateQuestionPage = !this.questions?.length;
        this.validateQuestions();
        this.constructDimensionCards();

        if (!this.selectedQuestion) {
            this.selectedQuestion = this.dimensionCards.find(card => card.questions.length)?.questions[0] ?? null;
        }
    }

    private validateQuestions() {
        this.hasInvalidQuestions = this.questions.some(x => !x.question?.trim() || !x.expectedAnswerSignals?.length);
    }

    private constructDimensionCards(): void {
        const cardExpansionMap = new Map(this.dimensionCards.map(card => [card.dimension, card.isExpanded]));
        const dimensionCards = Object.values(ViDimensionEnum)
            .map(dimension => {
                return {
                    dimension,
                    questions: this.questions.filter(x => x.dimension === dimension),
                    isExpanded: cardExpansionMap.get(dimension) ?? true
                };
            });

        this.dimensionCards = dimensionCards;
    }

    private scrollFirstErrorIntoView(): void {
        const invalidCard = this.dimensionCards.find(
            card => card.questions.some(question => !question.question?.trim() || !question.expectedAnswerSignals?.length)
        );
        if (!invalidCard) {
            return;
        }

        const cardComponent = this.dimensionCardComponents?.toArray().find(component => component.card === invalidCard);
        cardComponent?.scrollFirstErrorIntoView(this.reviewQuestionScrollPanel);
    }

    private appendQuestions(newQuestions: ViQuestionApi[] | null, selectLast: boolean = false): void {
        if (!newQuestions?.length) {
            return;
        }
        this.setQuestions([...this.questions, ...newQuestions]);
        if (selectLast) {
            this.selectedQuestion = newQuestions[newQuestions.length - 1];
        }
    }
}
